import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "388",
  "slug": "identify-country-by-license-plates",
  "title": "How to Identify a Country by Its License Plates",
  "excerpt": "License plates are one of the most reliable clues for figuring out where in the world you are. Here is how their colors, codes, and shapes give away the country at a glance.",
  "date": "2026-06-09",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1553708917-68ce200d86b2?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxldXJvcGVhbiUyMGxpY2Vuc2UlMjBwbGF0ZXxlbnwxfDB8fHwxNzgwOTkyODMxfDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "License plates reveal a country through their color, shape, and markings: the blue EU strip and country code in Europe, distinctive plate colors and formats by region, and the side of the road traffic drives on. Learning a handful of these patterns dramatically sharpens your location-guessing.",
  "content": [
    {
      "type": "paragraph",
      "text": "Ask any experienced location-guessing player what they look at first, and a surprising number will say \"the cars.\" License plates are everywhere on a street, they barely change for years, and they encode a startling amount of geographic information. Learn to read them and you can often narrow a mystery location to a single country in seconds. Here is the field guide."
    },
    {
      "type": "heading",
      "text": "Start With Color"
    },
    {
      "type": "paragraph",
      "text": "Plate color is the fastest, coarsest clue. Different parts of the world favour very different palettes, and the contrast between the plate and its text is often visible even in a blurry or distant shot. A few rough rules of thumb:"
    },
    {
      "type": "list",
      "items": [
        "White plates with black text are extremely common worldwide, so on their own they tell you little, but the details around them do.",
        "Yellow rear plates are a classic marker of the United Kingdom, the Netherlands, and a few others.",
        "Bright, varied colors and distinctive backgrounds often point toward specific regions or US states.",
        "A vertical colored strip on the left edge of the plate is a strong European signal."
      ]
    },
    {
      "type": "heading",
      "text": "The European Blue Strip"
    },
    {
      "type": "paragraph",
      "text": "If you see a slim blue band running down the left side of a plate, you are almost certainly in Europe. That blue strip carries the ring of EU stars and, crucially, a one- or two-letter country code: D for Germany, F for France, E for Spain, PL for Poland, NL for the Netherlands, and so on. Spotting and reading that code is one of the single most powerful moves in the whole hobby, because it can pin the country exactly."
    },
    {
      "type": "paragraph",
      "text": "Even non-EU European countries often use a similar format, sometimes with their own colored strip, so the overall \"tall plate with a colored band and a code\" look is a reliable European fingerprint."
    },
    {
      "type": "heading",
      "text": "Shape and Proportion"
    },
    {
      "type": "paragraph",
      "text": "Plates are not all the same size. Many European, Australian, and African plates are long and relatively short in height. North American plates tend to be more rectangular and chunkier. Some countries use distinctly square or two-line plates. The overall proportions are a useful tiebreaker when color alone is ambiguous."
    },
    {
      "type": "heading",
      "text": "Format and Characters"
    },
    {
      "type": "paragraph",
      "text": "The pattern of letters and numbers is another giveaway, even if you cannot read the plate clearly:"
    },
    {
      "type": "list",
      "items": [
        "Some countries use mostly numbers; others mix letters and numbers in a fixed pattern.",
        "A few regions use non-Latin scripts, which instantly narrows your search to specific parts of the world.",
        "Special plate colors for taxis, government, or commercial vehicles vary by country and can confirm a guess."
      ]
    },
    {
      "type": "heading",
      "text": "Pair Plates With the Side of the Road"
    },
    {
      "type": "paragraph",
      "text": "License plates work best in combination with other clues. The most important partner is which side of the road traffic drives on. If cars are driving on the left and you see a yellow rear plate, you have a very different shortlist than if they are on the right with a blue EU strip. Together, these two observations often collapse a whole continent down to a handful of candidates."
    },
    {
      "type": "heading",
      "text": "Why This Skill Transfers"
    },
    {
      "type": "paragraph",
      "text": "The beauty of reading plates is that it trains a broader instinct: noticing small, stable, human-made details and connecting them to geography. Road signs, road markings, utility poles, and architecture all work the same way. The more of these little systems you learn, the faster you can place a location without ever seeing a famous landmark."
    },
    {
      "type": "heading",
      "text": "Reading North American Plates"
    },
    {
      "type": "paragraph",
      "text": "North America plays by its own rules. In the United States, license plates are issued by individual states, and many states use distinctive colors, background images, and slogans, from mountain scenes to mottos printed across the plate. A keen eye can sometimes narrow a US location to a specific state from the plate design alone, and Canadian provinces do something similar. So in North America the plate is less about identifying the country, which is usually obvious from other clues, and more about zooming in on the exact region."
    },
    {
      "type": "paragraph",
      "text": "That regional specificity is a gift in a guessing game. While a European plate often hands you the country outright, a North American plate can quietly point you toward the right corner of a very large continent."
    },
    {
      "type": "paragraph",
      "text": "Practise these clues on street-level images where plates are visible. Check the plate alongside road signs or markings before choosing a country; a visiting vehicle can mislead you."
    }
  ]
};
